import {IMAGE_LOADER, ImageLoaderConfig} from '@angular/common';
import {Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, inject, ElementRef, Output, EventEmitter, NgZone} from '@angular/core';
import {ReportUserGenerated} from "../../../../core/model-store/models/report-user-generated";
import {MediaHelperService} from "../../../../core/media-helper.service";
import {listReportShowsObjectImages} from '../../generate-report-util';
import {allImageIds, fetchReportImageUrls, waitForImages} from '../report-images';

@Component({
    selector: 'app-report-list',
    templateUrl: './report-list.component.html',
    styleUrls: ['./report-list.component.css'],
    standalone: false,
    providers: [
      // DMS URLs are already absolute; pass them through unchanged.
      {
        provide: IMAGE_LOADER,
        useValue: (config: ImageLoaderConfig) => config.src,
      },
    ],
})
export class ReportListComponent implements OnChanges {
  // Injections
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly mediaService: MediaHelperService = inject(MediaHelperService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly ngZone: NgZone = inject(NgZone);

  @Input() reportTemplate: ReportUserGenerated;
  @Input() extractedArtifacts: any;
  @Input() viewOptionsCheckboxValues: any;
  @Input() preview: boolean = false;
  @Input() numPhotographsInReport: number = 1;
  @Input() selectedPhotographSize: string = 'small';

  /** Emits when every image has loaded or failed, so the PDF can be printed. */
  @Output() imagesReady = new EventEmitter<void>();

  reportHeaderColumns : string[] = [];
  imageUrls : Map<string, string> = new Map();
  private latestUrlRequest = 0;

  isAllImagesSelected() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'alle_bilder').value;
  }

  showObjectImages() {
    return listReportShowsObjectImages(
      this.numPhotographsInReport, this.isAllImagesSelected());
  }

  isShowTableHeader() {
    return this.reportHeaderColumns.length <= 3 && this.selectedPhotographSize !== 'large';
  }

  findFieldByKey(artifact: any, key: string) {
    let artifactList = [];

    artifact.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.key === key) {
          artifactList.push(field);
        }
      });
    })

    return artifactList;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.extractedArtifacts || changes.numPhotographsInReport) {
      this.loadImageUrls().then();
    }

    if (changes.extractedArtifacts && this.extractedArtifacts && this.extractedArtifacts.length > 0) {
      //Go through each extractedArtifacts.sections.section and add all unique field.key to this.reportHeaderColumns
      this.reportHeaderColumns = [];

      this.extractedArtifacts.forEach(artifact => {
        artifact.sections?.forEach(section => {
          section.fields.forEach(field => {
            if (!this.reportHeaderColumns.includes(field.key)) {
              this.reportHeaderColumns.push(field.key)
            }
          });
        });
      });

      console.log('NUM FIELDS SELECTED: ', this.reportHeaderColumns.length);
      this.cdRef.detectChanges();
    }
  }

  private async loadImageUrls() {
    const request = ++this.latestUrlRequest;
    const imageIds = this.showObjectImages() ? allImageIds(this.extractedArtifacts) : [];
    const missingImageIds = imageIds.filter(imageId => !this.imageUrls.has(imageId));
    const urls = await fetchReportImageUrls(this.mediaService, missingImageIds);
    if (request !== this.latestUrlRequest) {
      return;
    }
    this.imageUrls = new Map([...this.imageUrls, ...urls]);
    this.cdRef.detectChanges();
    // Outside Angular, or every image event checks the whole report again,
    // so rendering time grew with the square of the number of objects.
    await this.ngZone.runOutsideAngular(() => waitForImages(this.host.nativeElement));
    if (request === this.latestUrlRequest) {
      this.ngZone.run(() => this.imagesReady.emit());
    }
  }

}
